import React, { useCallback } from 'react';
import CollapsibleSection from './CollapsibleSection';
import TooltipLabel from '../common/TooltipLabel';
import { Trash2 } from 'lucide-react';
import { edgeUpdateManager } from '../../utils/sequentialUpdateManager';

// Labels-as-expressions (ruling 2026-08-02): the Label is the single authored
// routing construct on a connection. It is what the canvas displays AND — for
// branches out of a decision node — the expression the kernel's decision model
// evaluates against the captured answer:
//   "Not at all"        → matches when the answer equals that value
//   "!= Not at all"     → matches when the answer is anything else
//   ">= 5"              → numeric comparison against the answer
//   "Other"             → default branch, taken when nothing else matches
// The separate Condition field (and its structured-predicate export) is gone;
// legacy conditions still stored on old edges are honored at export time by
// synthesizing a label from them when no label was authored.

const EdgeEditor = ({ edge, onEdgeUpdate, onEdgeDelete, nodes = [], edges = [], sectionId, onUndock }) => {
  const getLabel = (nodeId) => {
    const node = nodes.find((n) => n.id === nodeId);
    if (!node) return nodeId;

    // Friendly labels per node type
    if (node.type === 'start') return 'START';
    if (node.type === 'end') return node.id;

    // For question nodes prefer Q-number or explicit id
    return (
      node.data?.id ||
      node.data?.question_id ||
      node.data?.questionId ||
      node.data?.variableName ||
      nodeId
    );
  };

  const handleChange = useCallback((field, value) => {
    onEdgeUpdate(edge.id, { [field]: value, selected: true });
  }, [edge.id, onEdgeUpdate]);

  const handleDataChange = useCallback(async (field, value) => {
    try {
      await edgeUpdateManager.enqueueUpdate(async () => {
        // Send only the changed field — handleEdgeUpdate will deep-merge
        // into the current edge data to avoid stale closure overwrites
        if (edge.data?.[field] !== value) {
          onEdgeUpdate(edge.id, { _dataFieldUpdate: { [field]: value }, selected: true });
        }
      }, `EdgeEditor.handleDataChange(${field})`);
    } catch (error) {
      // Fail fast - throw error up to error boundary
      throw new Error(`Failed to update edge data: ${error.message}`);
    }
  }, [edge.id, edge.data, onEdgeUpdate]);

  const sourceNode = nodes.find((n) => n.id === edge.source);
  const sourceIsDecision = sourceNode?.type === 'decision';

  // Auto-assign branch_id for edges from decision nodes (once per edge)
  React.useEffect(() => {
    if (sourceIsDecision && !edge.data?.branch_id) {
      const siblingsFromDecision = edges.filter(e => e.source === edge.source);
      const idx = siblingsFromDecision.findIndex(e => e.id === edge.id);
      const autoId = `branch_${idx + 1}`;
      handleDataChange('branch_id', autoId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edge.id]);

  // Banner: multiple edges from a non-decision node need distinct priorities to
  // disambiguate. Decision branches are exempt — their labels route them.
  const unconditionalFromSrc = sourceIsDecision ? [] : edges
    .filter(e => e.source === edge.source);
  const priorities = new Set(unconditionalFromSrc.map(e => Number(e.data && e.data.priority)).filter(n => Number.isFinite(n)));
  const needsPriorityBanner = unconditionalFromSrc.length > 1 && priorities.size !== unconditionalFromSrc.length;

  const assignDistinctPriorities = React.useCallback(() => {
    if (!needsPriorityBanner) return;
    // Compute descending priorities and apply
    const sorted = [...unconditionalFromSrc].sort((a, b) => ((Number(b.data?.priority) || 0) - (Number(a.data?.priority) || 0)));
    let pri = sorted.length;
    sorted.forEach((ed) => {
      const newPri = pri--;
      if (ed.data?.priority !== newPri) {
        onEdgeUpdate(ed.id, { data: { ...(ed.data || {}), priority: newPri } });
      }
    });
  }, [needsPriorityBanner, unconditionalFromSrc, onEdgeUpdate]);

  // Warn when a decision branch has no label — an unlabeled branch gives the
  // kernel nothing to route on (this is exactly what misrouted Rand Health).
  const decisionBranchNeedsLabel = sourceIsDecision && !(edge.data?.label || '').trim();

  return (
    <CollapsibleSection title="Connection Editor" defaultCollapsed={false} sectionId={sectionId} onUndock={onUndock}>
      {needsPriorityBanner && (
        <div style={{
          marginBottom: '8px', padding: '8px', background: '#fff3cd', border: '1px solid #ffeeba', borderRadius: 4,
          color: '#856404', fontSize: '12px'
        }}>
          Multiple connections from this node require distinct numeric priorities.
          <button className="btn-small" style={{ marginLeft: 8 }} onClick={assignDistinctPriorities} title="Automatically assign unique priority numbers to resolve ambiguity">Assign distinct priorities</button>
        </div>
      )}
      <div style={{ textAlign: 'right', marginBottom: '4px' }}>
        {onEdgeDelete && (
          <button
            style={{ background: 'none', border: 'none', color: '#d9534f', cursor: 'pointer' }}
            onClick={() => onEdgeDelete(edge.id)}
            title="Delete connection"
          ><Trash2 size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} /> Delete</button>)}
      </div>

      <div className="form-group">
        <TooltipLabel keyName="condition">Connection</TooltipLabel>
        <div style={{ padding: '8px', backgroundColor: '#f8f9fa', borderRadius: '4px', fontSize: '12px' }}>
          From: <strong>{getLabel(edge.source)}</strong> → To: <strong>{getLabel(edge.target)}</strong>
        </div>
      </div>

      <div className="form-group">
        <TooltipLabel htmlFor="edge-label" keyName="label">Label</TooltipLabel>
        <input
          id="edge-label"
          type="text"
          value={edge.data?.label || ''}
          onChange={(e) => handleDataChange('label', e.target.value)}
          placeholder={sourceIsDecision ? 'Not at all, != Not at all, >= 5, Other' : 'Yes, No, Other, etc.'}
        />
        {sourceIsDecision && (
          <small style={{ color: '#666', fontSize: '12px' }}>
            The label is this branch's routing expression. A plain value means
            "the answer equals this"; operators like != or &gt;= express the rest;
            "Other" is the catch-all taken when no other branch matches.
          </small>
        )}
      </div>

      {decisionBranchNeedsLabel && (
        <div style={{
          marginBottom: '8px', padding: '8px', background: '#f8d7da',
          border: '1px solid #f5c2c7', borderRadius: 4, color: '#842029', fontSize: '12px'
        }}>
          ⚠️ This branch has no label, so the survey cannot route it. Enter the
          answer value (or expression) that should take this branch.
        </div>
      )}

      <div className="form-group">
        <TooltipLabel htmlFor="edge-priority" keyName="priority">Priority</TooltipLabel>
        <input
          id="edge-priority"
          type="number"
          value={edge.data?.priority || 1}
          onChange={(e) => handleDataChange('priority', parseInt(e.target.value) || 1)}
          min="1"
          max="10"
        />
        <small style={{ color: '#666', fontSize: '12px' }}>
          Higher numbers = higher priority when multiple connections leave a node
        </small>
      </div>

      <div className="form-group">
        <TooltipLabel htmlFor="edge-type" keyName="condition">Connection Style</TooltipLabel>
        <select
          id="edge-type"
          value={edge.type || 'default'}
          onChange={(e) => {
            handleChange('type', e.target.value);
            handleDataChange('edgeType', e.target.value);
          }}
        >
          <option value="default">Default</option>
          <option value="smoothstep">Smooth Step</option>
          <option value="step">Step</option>
          <option value="straight">Straight</option>
        </select>
      </div>

      <div className="form-group">
        <TooltipLabel htmlFor="edge-style" keyName="condition">Connection Line Type</TooltipLabel>
        <select
          id="edge-style"
          value={edge.data?.style || 'normal'}
          onChange={(e) => handleDataChange('style', e.target.value)}
        >
          <option value="normal">Normal</option>
          <option value="conditional">Conditional (Dashed)</option>
          <option value="fallback">Fallback (Dotted)</option>
          <option value="package">Package Flow</option>
        </select>
      </div>

      <div className="form-group">
        <TooltipLabel htmlFor="edge-description" keyName="description">Description</TooltipLabel>
        <textarea
          id="edge-description"
          value={edge.data?.description || ''}
          onChange={(e) => handleDataChange('description', e.target.value)}
          placeholder="Describe when this path should be taken"
          rows={2}
        />
      </div>

      <div className="form-group">
        <label htmlFor="edge-weight">Weight</label>
        <input
          id="edge-weight"
          type="number"
          value={edge.data?.weight || 1}
          onChange={(e) => handleDataChange('weight', parseFloat(e.target.value) || 1)}
          step="0.1"
          min="0"
          max="1"
        />
        <small style={{ color: '#666', fontSize: '12px' }}>
          Probability weight for randomized routing (0.0 - 1.0)
        </small>
      </div>

      {sourceIsDecision && (
        <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#e8f4fd', borderRadius: '4px' }}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#0066cc' }}>
            Label Examples:
          </h4>
          <ul style={{ margin: 0, paddingLeft: '15px', fontSize: '12px', color: '#333' }}>
            <li><code>Not at all</code> - take this branch when the answer is "Not at all"</li>
            <li><code>!= Not at all</code> - take this branch for any other answer</li>
            <li><code>&gt;= 5</code> - numeric answers of 5 or more</li>
            <li><code>Other</code> - catch-all when no other branch matches</li>
          </ul>
        </div>
      )}
    </CollapsibleSection>
  );
};

export default EdgeEditor;
